import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { IBizEntity, BizEntityTypeCategory } from '@optima-stride-ai/core';
import { LibraryPage, LibraryService } from '../library.service';
import { CollaboraService } from '../../../services/collabora.service';
import { ViewerManagerService } from '../../../services/viewer-manager.service';
import { NavigationStateService } from '../../../services/navigation-state.service';
import { ThumbnailManagerService } from '../../../services/thumbnail-manager.service';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { ContextMenuService } from '../../../context-menu/context-menu.service';
import { environment } from '../../../../environments/environment';

/**
 * Facade service that consolidates multiple library-related services
 * into a single interface, reducing coupling in components
 */
@Injectable({
  providedIn: 'root'
})
export class LibraryFacadeService {
  // Inject all related services
  private readonly libraryService = inject(LibraryService);
  private readonly collaboraService = inject(CollaboraService);
  private readonly viewerManagerService = inject(ViewerManagerService);
  private readonly navigationStateService = inject(NavigationStateService);
  private readonly thumbnailManagerService = inject(ThumbnailManagerService);
  private readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private readonly contextMenuService = inject(ContextMenuService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Expose commonly used methods through the facade

  /**
   * Get children entities by parent ID
   */
  async getBizEntityByParent(parentId: string): Promise<IBizEntity[]> {
    return this.libraryService.getBizEntityByParent(parentId);
  }

  async getBizEntityPage(
    parentId: string | undefined,
    limit: number,
    offset: number,
    category?: BizEntityTypeCategory | BizEntityTypeCategory[]
  ): Promise<LibraryPage> {
    return this.libraryService.getBizEntityPage(parentId, limit, offset, category);
  }

  /**
   * Get children entities by parent ID and filter by category
   */
  async getBizEntityByParentAndCategory(parentId: string, category: BizEntityTypeCategory): Promise<IBizEntity[] | undefined> {
    return this.bizEntityStateStoreService.getState(
      this.bizEntityTypeStateStoreService,
      undefined,
      undefined,
      parentId,
      undefined,
      undefined,
      [category],
      undefined,
      undefined
    );
  }

  /**
   * Get all entities from state store
   */
  async getAllBizEntities(): Promise<IBizEntity[] | undefined> {
    return this.bizEntityStateStoreService.getState(this.bizEntityTypeStateStoreService);
  }

  /**
   * Get entities by category
   */
  async getBizEntitiesByCategory(category: BizEntityTypeCategory): Promise<IBizEntity[] | undefined> {
    return this.bizEntityStateStoreService.getState(
      this.bizEntityTypeStateStoreService,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      [category],
      undefined,
      undefined
    );
  }

  /**
   * Check if file is supported by Collabora
   */
  // isFileSupported(fileName: string): boolean {
  //   return this.collaboraService.isFileSupported(fileName);
  // }

    isFileSupported(category: BizEntityTypeCategory): boolean {
    return this.collaboraService.isFileSupported(category);
  }

  /**
   * Check if Collabora is enabled
   */
  get isCollaboraEnabled(): boolean {
    return environment.collaboraEnabled;
  }

  /**
   * Check if viewer is currently opening
   */
  isViewerOpening(): boolean {
    return this.viewerManagerService.isCurrentlyOpeningViewer();
  }

  /**
   * Check if viewer is open
   */
  isViewerOpen(): boolean {
    return this.viewerManagerService.isOpen();
  }

  /**
   * Open a viewer for a file
   */
  openViewer(file: IBizEntity): boolean {
    return this.viewerManagerService.openViewer(file);
  }

  /**
   * Close the current viewer
   */
  closeViewer(): void {
    this.viewerManagerService.closeViewer();
  }

  /**
   * Update current selected item in navigation state
   */
  updateCurrentSelectedItem(itemId: string): void {
    this.navigationStateService.updateCurrentSelectedItem(itemId);
  }

  /**
   * Get current selected item from navigation state
   */
  getCurrentSelectedItem(): string | null {
    return this.navigationStateService.getCurrentSelectedItem();
  }

  /**
   * Set original navigation attributes
   */
  setOriginalNavAttributes(navAttributes: any): void {
    this.navigationStateService.setOriginalNavAttributes(navAttributes);
  }

  /**
   * Get context menu items
   */
  getContextMenuItems(libraryData?: IBizEntity): any[] {
      // ⚠️ TEMPORARY PATCH FOR TESTING - Apply default rights if entity lacks rights
    // TODO: Remove this once backend properly sends rights in all entity responses
    const entityWithRights = libraryData ? {
      ...libraryData,
      rights: libraryData.rights
    } as IBizEntity : undefined;
    return this.contextMenuService.getMenu(entityWithRights);
  }

  /**
   * Generate thumbnail for a file
   */
  async generateThumbnailForFile(file: File): Promise<string | null> {
    return this.thumbnailManagerService.generateThumbnailForFile(file);
  }

  /**
   * Generate markdown thumbnail for new file
   */
  async generateMarkdownThumbnailForNewFile(content: string): Promise<string | null> {
    return this.thumbnailManagerService.generateMarkdownThumbnailForNewFile(content);
  }

  /**
   * Convert base64 to File
   */
  base64ToFile(dataUrl: string, filename: string): File {
    return this.libraryService.base64ToFile(dataUrl, filename);
  }

  /**
   * Navigate to route with query params
   */
  navigateWithQueryParams(queryParams: any): Promise<boolean> {
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  /**
   * Get current router URL
   */
  get currentUrl(): string {
    return this.router.url;
  }

  /**
   * Get query params observable
   */
  get queryParams$() {
    return this.route.queryParams;
  }

  /**
   * Get a single entity by ID
   */
  async getBizEntityById(entityId: string): Promise<IBizEntity | null> {
    return this.libraryService.getBizEntityById(entityId);
  }
}
